/// <amd-module name="Test/TestPlansLibrary/Pivots/TestCasesPivot/Component/TestCasesTabConnectedComponent" />
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";
import * as Constants from "../TestCasePivotConstants";
import * as Resources from "Test/TestPlansLibrary/Pivots/TestCasesPivot/Resources";
import { TestCasesTableItemProvider } from "./TestCasesTableItemProvider";
import { TestCasesActions } from "../Redux/TestCasesActions";
import { ITestCasesAwareState, ITestCaseItem } from "../Redux/TestCasesContracts";
import { getTestCasesModule } from "../Redux/TestCasesModule";
import {
    getTestCaseItemsFromState,
    getWorkItemDataProviderStateFromState,
    getIsLoadingFromState,
    getColumnOptionFields
} from "../Redux/TestCasesSelector";
import { TelemetryConstants, TestPlansLibraryConstants, WorkItemConstants } from "Test/TestPlansLibrary/LibraryHub/Constants";
import { ITestPlansLibraryLocalContext } from "Test/TestPlansLibrary/LibraryHub/Redux/TestPlansLibraryContracts";
import { TestPlansLibraryLocalContext } from "Test/TestPlansLibrary/LibraryHub/Utils/TestPlansLibraryLocalContextProvider";
import { hasMoreTestCases } from "Test/TestPlansLibrary/LibraryHub/Utils/TestPlansLibraryUtils";
import { ITestCasesPivotContextMenuParams } from "Test/TestPlansLibrary/TestCasesPivotMenuService/TestCasesPivotMenuService.types";
import { getAllColumnFields } from "Test/TestPlansLibrary/ColumnOptions/TestCasesColumnOptionUtils";
import { ITfsPageService } from "Tfs/Platform/Page";

import { IReadonlyObservableArray, ObservableArray, ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssComponentContext, IVssComponentProperties, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { routeUrl } from "VSS/Platform/Location";
import { TTIMonitorActions } from "VSS/Redux/Modules/TTIMonitor";
import { CustomCard } from "VSSUI/Card";
import { IListSelection, ListSelection } from "VSSUI/List";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Pill, PillVariant } from "VSSUI/Pill";
import { PillGroup } from "VSSUI/PillGroup";
import {
    ColumnSelect,
    ColumnMore,
    ITableColumn,
    renderSimpleCell,
    renderSimpleCellValue,
    Table,
    SimpleTableCell,
    ITableRowDetails,
    TableLoadingRow
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";

export interface ITestCasesTabComponentProps extends IVssComponentProperties {
    // Test case items to render
    testCases: ITestCaseItem[];

    // Prop to show loading experience
    isLoading: boolean;

    // The react context object which is provided by TestPlansLibraryConnectedView
    testPlansLibraryContext?: ITestPlansLibraryLocalContext;

    // Indicator if there are more test cases to load
    hasMoreTestCases?: boolean;

    // array containing the names of all the column option fields to be displayed
    columnOptionFields?: string[];

    // Callback that dispatches an action to load next test case page
    loadTestCasesPage(): void;
}

/**
 * TestCasesTabContentComponent: Renders the test cases pivot in test plans library
 */
export class TestCasesTabContentComponent extends VssComponent<ITestCasesTabComponentProps, IVssComponentContext> {
    private _items: ObservableArray<ITestCaseItem | ObservableValue<ITestCaseItem | undefined>>;
    private _testCasesTableSelection: IListSelection;
    private _provider: TestCasesTableItemProvider;
    private _columnSelect: ColumnSelect;
    private _showLinkedItemsPanel: ObservableValue<boolean>;
    private _tableColumns: ITableColumn<any>[];

    constructor(props: ITestCasesTabComponentProps, context?: IVssComponentContext) {
        super(props, context);
        this._items = new ObservableArray<ITestCaseItem | ObservableValue<ITestCaseItem | undefined>>();
        this._testCasesTableSelection = new ListSelection(true);
        this._provider = new TestCasesTableItemProvider();
        this._columnSelect = new ColumnSelect();
        this._showLinkedItemsPanel = new ObservableValue<boolean>(false);
        this._tableColumns = this._getColumns();
    }

    public shouldComponentUpdate(nextProps: ITestCasesTabComponentProps) {
        if (this.props.columnOptionFields !== nextProps.columnOptionFields) {
            this._tableColumns = this._getColumns(nextProps.columnOptionFields);
        }
        this._items = this._provider.getTestCaseItems(
            nextProps.testCases,
            this._testCasesTableSelection,
            nextProps.hasMoreTestCases || nextProps.isLoading
        );
        return true;
    }

    public componentDidUpdate() {
        this._markTTIPerf();
    }

    public render(): JSX.Element {
        return (
            <div className="library-testcases-tab">
                <DynamicModuleLoader modules={[getTestCasesModule()]}>
                    <WrappedComponent
                        dependencies={["ms.vss-test-web.test-cases-pivot-filter", "ms.vss-test-web.testplans-library-filter-component"]}
                        wrappedType={"testcases-pivot-filter-component"}
                    />
                    <div className="flex-row">
                        <CustomCard
                            className="testplans-library-testcases-card bolt-card-with-header"
                            headerClassName="bolt-card-header bolt-header-with-commandbar"
                        >
                            <Table
                                className="testplans-library-testcases-table"
                                containerClassName="custom-scrollbar"
                                columns={this._tableColumns}
                                showLines={true}
                                scrollable={true}
                                itemProvider={this._items}
                                singleClickActivation={false}
                                onActivate={this._openTestCaseWITForm}
                                selection={this._testCasesTableSelection}
                                renderLoadingRow={(rowIndex: number, rowDetails: ITableRowDetails<any>) => {
                                    if (this.props.hasMoreTestCases) {
                                        this.props.loadTestCasesPage();
                                    }

                                    return (
                                        <TableLoadingRow
                                            columns={this._getColumns(this.props.columnOptionFields)}
                                            key={rowIndex}
                                            details={rowDetails}
                                            rowIndex={rowIndex}
                                        />
                                    );
                                }}
                            />
                        </CustomCard>
                    </div>
                </DynamicModuleLoader>
                <Observer showPanel={this._showLinkedItemsPanel}>
                    {(props: { showPanel: boolean }) => {
                        return (
                            props.showPanel && (
                                <WrappedComponent
                                    dependencies={["ms.vss-test-web.testcase-linked-items"]}
                                    wrappedType="testcase-linked-items-panel"
                                    wrappedProps={{
                                        showPanel: this._showLinkedItemsPanel,
                                        testCaseName: this._getSelectedTestCaseName(this._testCasesTableSelection, this.props.testCases),
                                        testCaseId: this._getSelectedTestCaseId(this._testCasesTableSelection, this.props.testCases)
                                    }}
                                />
                            )
                        );
                    }}
                </Observer>
            </div>
        );
    }

    private _openTestCaseWITForm = () => {
        const testCaseIndex = this._testCasesTableSelection.value[0].beginIndex;
        const testCaseId = this.props.testCases[testCaseIndex].id;

        const tfsPageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        let projectName: string = "";

        if (tfsPageData && tfsPageData.project) {
            projectName = tfsPageData.project.name;
        }

        const workItemUrl: string = routeUrl(this.context.pageContext, "ms.vss-test-web.testplans-workitem-page-route", {
            project: projectName,
            mode: WorkItemConstants.WorkItemEditMode,
            type: TestPlansLibraryConstants.TestCaseTabId,
            id: String(testCaseId)
        });

        FastPageSwitch(this.context.pageContext, workItemUrl, true);
    };

    private _getColumns(columnOptionFields?: string[]): ITableColumn<any>[] {
        const tableColumns: ITableColumn<any>[] = [];
        const menuItemColumn = new ColumnMore(
            () => {
                return {
                    id: "test-case-workitem-context-menu-column",
                    items: this._getTestCaseContextMenuItemsFromProvider()
                };
            },
            undefined,
            (rowindex: number, columnIndex: number) => {
                if (!this._testCasesTableSelection.selected(rowindex)) {
                    this._testCasesTableSelection.select(rowindex, 1, false);
                }
            }
        );

        tableColumns.push(
            this._columnSelect,
            {
                id: Constants.Id,
                name: Resources.Id,
                width: 100,
                renderCell: renderSimpleCell
            },
            {
                id: Constants.Title,
                name: Resources.Title,
                width: 300,
                renderCell: renderSimpleCell
            },
            menuItemColumn
        );

        if (columnOptionFields) {
            const fields = getAllColumnFields(columnOptionFields);
            for (const columnOptionField of fields) {
                if (columnOptionField.id === WorkItemConstants.WitTagsField) {
                    // handle rendering of tags separately
                    tableColumns.push({
                        id: columnOptionField.id,
                        name: columnOptionField.name,
                        width: 100,
                        renderCell: this._renderTagsCell,
                        className: "body-m"
                    });
                } else {
                    tableColumns.push({
                        id: columnOptionField.id,
                        name: columnOptionField.name,
                        width: 100,
                        renderCell: renderSimpleCell,
                        className: "body-m"
                    });
                }
            }
        }
        return tableColumns;
    }

    private _renderTagsCell(rowIndex: number, columnIndex: number, tableCell: ITableColumn<{}>, tableItem: any) {
        let tags: string[] = [];
        if (tableItem.tags) {
            tags = tableItem.tags.split(";");
        }
        let ellipsis: JSX.Element = <></>;
        /*  If there are more than 2 tags in a cell, show an ellipsis after the first two tags. 
            Rest of the tags will be shown as a tooltip when hover on the ellipsis */
        if (tags.length > TestPlansLibraryConstants.MaxTagsInACell) {
            const tooltip = tags.splice(TestPlansLibraryConstants.MaxTagsInACell).join(";");
            ellipsis = (
                <Pill variant={PillVariant.outlined}>
                    <Tooltip renderContent={() => <div>{tooltip}</div>}>
                        <span>{"..."}</span>
                    </Tooltip>
                </Pill>
            );
        }
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableCell} key={"col-" + columnIndex}>
                <PillGroup>
                    {tags.map(tag => (
                        <Pill className="tag-width" variant={PillVariant.outlined} key={tag}>
                            <Tooltip renderContent={() => <div>{tag}</div>}>
                                <span>{tag}</span>
                            </Tooltip>
                        </Pill>
                    ))}
                    {ellipsis}
                </PillGroup>
            </SimpleTableCell>
        );
    }

    private _renderAssignedToCell(rowIndex: number, columnIndex: number, tableCell: ITableColumn<{}>, tableItem: any) {
        let assignedToName = "";
        if (tableItem && tableItem.assignedTo && tableItem.assignedTo.displayName) {
            assignedToName = tableItem.assignedTo.displayName;
        }

        return renderSimpleCellValue(columnIndex, tableCell, assignedToName);
    }

    private _getTestCaseContextMenuItemsFromProvider = (): IReadonlyObservableArray<IMenuItem> => {
        const menuItems: ObservableCollection<IMenuItem> = new ObservableCollection();
        const props: ITestCasesPivotContextMenuParams = {
            selection: this._testCasesTableSelection,
            testCases: this.props.testCases,
            linkedItemsPanelOpen: this._openLinkedItemsPanel
        };
        menuItems.push(getMenuItems(this.context.pageContext, "ms.vss-test-web.test-case-workitem-context-menu", props));
        return menuItems;
    };

    public _openLinkedItemsPanel = () => {
        this._showLinkedItemsPanel.value = true;
    };

    private _getSelectedTestCaseName(testCasesTableSelection: IListSelection, testCases: ITestCaseItem[]): string {
        const testCaseIndex = testCasesTableSelection.value[0].beginIndex;
        const testCaseName: string = testCases[testCaseIndex].title;
        return testCaseName;
    }

    private _getSelectedTestCaseId(testCasesTableSelection: IListSelection, testCases: ITestCaseItem[]): number {
        const testCaseIndex = testCasesTableSelection.value[0].beginIndex;
        const testCaseId: number = testCases[testCaseIndex].id;
        return testCaseId;
    }

    private _markTTIPerf() {
        if (this.props.testCases && this.props.testPlansLibraryContext) {
            if (this.props.testPlansLibraryContext.isPageLoad()) {
                // Mark TTI for page load scenario
                this.props.markTTI(TelemetryConstants.TEST_HUB_FEATURE_AREA_NAME, TelemetryConstants.TESTCASES_PIVOT_PAGE_LOAD_TTI_SCENARIO, {
                    ttiScenario: "PageLoad"
                });
                this.props.testPlansLibraryContext.setPageLoad(false);
            }
        }
    }
}

function mapStateToProps(state: ITestCasesAwareState): Partial<ITestCasesTabComponentProps> {
    const workItemDataProviderState = getWorkItemDataProviderStateFromState(state);
    
    return {
        testCases: getTestCaseItemsFromState(state),
        workItemDataProviderState: workItemDataProviderState,
        hasMoreTestCases: hasMoreTestCases(workItemDataProviderState),
        isLoading: getIsLoadingFromState(state),
        columnOptionFields: getColumnOptionFields(state)
    };
}

function mapDispatchToProps(dispatch: Dispatch): Partial<ITestCasesTabComponentProps> {
    return {
        markTTI: (featureName: string, scenarioName: string, additionalProperties?: any) =>
            dispatch(TTIMonitorActions.markTTI(featureName, scenarioName, additionalProperties)),
        loadTestCasesPage: () => dispatch(TestCasesActions.loadTestCasesPage()),
        dispatch
    };
}

/**
 * TestCasesTabFunctionalComponent: This functional component is just a dumb wrapper over TestCasesTabContentComponent.
 * We use this because React.useContext can only be invoked in function components. We get the context here and then pass is as props to the class component.
 */
export function TestCasesTabFunctionalComponent(props: ITestCasesTabComponentProps): JSX.Element {
    const testPlansLibraryLocalContext: ITestPlansLibraryLocalContext = React.useContext(TestPlansLibraryLocalContext);

    return <TestCasesTabContentComponent {...props} testPlansLibraryContext={testPlansLibraryLocalContext} />;
}

export const TestCasesTabConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(TestCasesTabFunctionalComponent);


VssComponent.register("testCasesPivot", TestCasesTabConnectedComponent  as ((props: ITestCasesTabComponentProps) => JSX.Element));
